import type { FileMetadata, FilePreviewProps, PreviewState } from './types'

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png']
const DOCUMENT_EXTENSIONS = ['doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx']

export type PreviewEnvironment = {
    fetch: typeof fetch
    createSocket: (url: string) => WebSocket
    createObjectURL: (blob: Blob) => string
    revokeObjectURL: (url: string) => void
    baseUrl: string
    headers: Record<string, string>
    credentials: RequestCredentials
    conversionTimeout?: number
}

export function browserEnvironment(): PreviewEnvironment {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    const token = document.getElementsByName('csrf-token')[0]?.getAttribute('content')
    const corporation = document.getElementById('__custom_route_data')?.getAttribute('data-corpId')
    if (token) headers['X-CSRF-TOKEN'] = token
    if (corporation) headers['X-CORP-CONTEXT'] = corporation
    return {
        baseUrl: `${process.env.ENV === 'local' ? process.env.API_URL : window.location.origin}/api/file/preview`,
        credentials: process.env.ENV === 'local' ? 'include' : 'same-origin',
        headers,
        fetch: window.fetch.bind(window),
        createSocket: (url) => new WebSocket(url),
        createObjectURL: (blob) => URL.createObjectURL(blob),
        revokeObjectURL: (url) => URL.revokeObjectURL(url),
    }
}

export function httpUrl(value: string): string | undefined {
    try {
        const url = new URL(value)
        return ['https:', 'http:'].includes(url.protocol) ? url.href : undefined
    } catch {
        return undefined
    }
}

function checkAbort(signal: AbortSignal) {
    if (signal.aborted) throw new DOMException('Preview closed', 'AbortError')
}

/** The converter protocol already used by candidate-page, authenticated with our auth response. */
function convertedUrl(metadata: FileMetadata, environment: PreviewEnvironment, signal: AbortSignal): Promise<string> {
    return new Promise((resolve, reject) => {
        checkAbort(signal)
        const authUrl = new URL(metadata.authUrl || '')
        if (!['ws:', 'wss:'].includes(authUrl.protocol)) throw new Error('Invalid converter URL')
        const socket = environment.createSocket(authUrl.href)
        let settled = false
        const finish = (error?: Error, url?: string) => {
            if (settled) return
            settled = true
            clearTimeout(timer)
            signal.removeEventListener('abort', abort)
            socket.onopen = socket.onmessage = socket.onerror = socket.onclose = null
            socket.close()
            if (error) reject(error)
            else resolve(url!)
        }
        const abort = () => finish(new DOMException('Preview closed', 'AbortError'))
        const timer = setTimeout(
            () => finish(new Error('Conversion timed out')),
            environment.conversionTimeout ?? 30000
        )
        signal.addEventListener('abort', abort, { once: true })
        socket.onopen = () => {
            try {
                socket.send(
                    JSON.stringify({
                        action: 'v2',
                        data: {
                            fileId: metadata.fileId,
                            folder: metadata.folder,
                            subscriptionId: metadata.subscriptionId,
                            extension: metadata.extension,
                        },
                    })
                )
            } catch {
                finish(new Error('Unable to request conversion'))
            }
        }
        socket.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data)
                if (typeof data.url === 'string') {
                    const url = httpUrl(data.url)
                    if (!url) throw new Error('Invalid binary URL')
                    finish(undefined, url)
                } else if (data.webSocketStatus === 'error' || data.statusCode === '404') {
                    finish(new Error('Conversion failed'))
                }
                // Pending/done without a URL are not a file. Keep waiting, with a bounded timeout.
            } catch {
                finish(new Error('Invalid conversion response'))
            }
        }
        socket.onerror = () => finish(new Error('Converter connection failed'))
        socket.onclose = () => finish(new Error('Converter disconnected before returning a file'))
    })
}

/** Each session owns its requests, socket and object URL; disposal also ignores late responses. */
export function startPreview(
    props: FilePreviewProps,
    environment: PreviewEnvironment,
    onState: (state: PreviewState) => void
) {
    const controller = new AbortController()
    const { signal } = controller
    let objectUrl: string | undefined
    let metadata: FileMetadata | undefined
    const post = (path: string, body: FilePreviewProps) =>
        environment.fetch(`${environment.baseUrl}/${path}`, {
            method: 'POST',
            headers: environment.headers,
            credentials: environment.credentials,
            body: JSON.stringify(body),
            signal,
        })

    const done = (async () => {
        try {
            const response = await post('auth', props)
            checkAbort(signal)
            if (!response.ok) {
                onState({ status: response.status >= 400 && response.status < 500 ? 'denied' : 'error' })
                return
            }
            const auth = await response.json()
            checkAbort(signal)
            if (auth.success !== true) {
                onState({ status: 'denied' })
                return
            }
            const data = auth.data as FileMetadata | undefined
            if (!data || !data.fileId || data.folder !== props.folder || typeof data.name !== 'string') {
                throw new Error('Invalid file authorization response')
            }
            metadata = data
            const extension = (metadata.extension || '').toLowerCase()
            const kind = IMAGE_EXTENSIONS.includes(extension) ? 'image' : 'pdf'
            if (
                extension !== 'pdf' &&
                !IMAGE_EXTENSIONS.includes(extension) &&
                !DOCUMENT_EXTENSIONS.includes(extension)
            ) {
                onState({ status: 'unsupported', metadata })
                return
            }
            if (metadata.scanStatus === 2 || (extension !== 'pdf' && !metadata.authUrl)) {
                onState({ status: 'unavailable', metadata })
                return
            }
            onState({ status: 'loading', metadata, kind })
            checkAbort(signal)
            let binary: Response
            if (extension === 'pdf') {
                // Temporary files additionally need profile UID, file UID and type, not returned by auth.
                const body: FilePreviewProps =
                    props.folder === 'tmp-apply-file'
                        ? props
                        : { fileId: Number(metadata.fileId), folder: props.folder }
                binary = await post('binary', body)
            } else {
                const url = await convertedUrl(metadata, environment, signal)
                checkAbort(signal)
                // Converter returns a signed URL. Never forward session/CSRF headers to that host.
                binary = await environment.fetch(url, { signal, credentials: 'omit' })
            }
            checkAbort(signal)
            if (!binary.ok) {
                onState({ status: [401, 403].includes(binary.status) ? 'denied' : 'error', metadata })
                return
            }
            let blob = await binary.blob()
            checkAbort(signal)
            if (!blob.size || /json|text\/html/.test(blob.type)) throw new Error('Invalid file response')
            if (kind === 'pdf') {
                const signature = await blob.slice(0, 1024).text()
                checkAbort(signal)
                if (!signature.includes('%PDF-')) throw new Error('Not a PDF file')
                // Signed storage URLs can return application/octet-stream; printing needs the PDF MIME type.
                if (blob.type !== 'application/pdf') blob = new Blob([blob], { type: 'application/pdf' })
            }
            objectUrl = environment.createObjectURL(blob)
            onState({ status: 'ready', metadata, kind, blob, url: objectUrl })
        } catch {
            if (!signal.aborted) onState({ status: 'error', metadata })
        }
    })()

    return {
        done,
        dispose() {
            controller.abort()
            if (objectUrl) {
                environment.revokeObjectURL(objectUrl)
                objectUrl = undefined
            }
        },
    }
}
